import { supabase } from '@/lib/supabase';
import { getTenantId } from '@/repos/contentRepo';
import type { Profile, UserTenant, UserRole, SystemUser } from '@/types/auth';

export async function createSystemUser(userData: { auth_user_id: string; email: string; name: string }): Promise<SystemUser> {
  console.log('🔍 createSystemUser iniciado para:', userData);
  
  try {
    console.log('1️⃣ Inserindo usuário na tabela users...');
    const { data, error } = await supabase
      .from('users')
      .insert([userData])
      .select()
      .single();

    if (error) {
      console.error('❌ Erro ao inserir usuário:', error);
      throw error;
    }
    
    console.log('2️⃣ Usuário inserido com sucesso:', data);
    
    // Garantir que o ID seja tratado como string
    const result = { ...data, id: data.id.toString() } as SystemUser;
    console.log('3️⃣ Resultado final:', result);
    
    return result;
    
  } catch (error) {
    console.error('❌ Erro em createSystemUser:', error);
    throw error;
  }
}

export async function getSystemUser(authUserId: string): Promise<SystemUser | null> {
  console.log('🔍 getSystemUser iniciado para authUserId:', authUserId);
  
  const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    try {
      return await Promise.race([
        promise,
        new Promise<T>((_, reject) => {
          timeoutHandle = setTimeout(() => {
            reject(new Error(`Timeout: ${label} demorou mais de ${ms}ms`));
          }, ms);
        }),
      ]);
    } finally {
      if (timeoutHandle) clearTimeout(timeoutHandle);
    }
  };

  try {
    // Step 1: Try simple query first to test table access
    console.log('1️⃣ Testando query simples (sem filtros)...');
    const { data: simpleData, error: simpleError } = await withTimeout(
      supabase
        .from('users')
        .select('*')
        .limit(1),
      5000,
      'getSystemUser query simples (users select limit 1)'
    );
    
    if (simpleError) {
      console.error('❌ Erro na query simples:', simpleError);
      throw simpleError;
    }
    console.log('✅ Query simples funcionou, dados:', simpleData);

    // Step 2: Check if auth_user_id field exists
    console.log('2️⃣ Verificando estrutura da tabela...');
    if (simpleData && simpleData.length > 0) {
      const sampleUser = simpleData[0];
      console.log('📝 Estrutura da tabela:', Object.keys(sampleUser));
      console.log('📝 Campo auth_user_id existe:', 'auth_user_id' in sampleUser);
      console.log('📝 Dados do usuário de exemplo:', sampleUser);
      
      // Verificar se há algum campo que possa ser o ID do usuário do Supabase
      const possibleIdFields = ['id', 'user_id', 'auth_user_id', 'supabase_user_id', 'uuid'];
      for (const field of possibleIdFields) {
        if (field in sampleUser) {
          console.log(`📝 Campo ${field} encontrado com valor:`, sampleUser[field]);
        }
      }
      
      // Se não existir auth_user_id, tentar usar o campo id diretamente
      if (!('auth_user_id' in sampleUser)) {
        console.log('⚠️ Campo auth_user_id não existe, tentando usar campo id...');
        console.log('📝 authUserId sendo usado:', authUserId);
        console.log('📝 Tipo do authUserId:', typeof authUserId);
        
        // Tentar buscar pelo campo id diretamente
        const { data: directData, error: directError } = await withTimeout(
          supabase
            .from('users')
            .select('*')
            .eq('id', authUserId)
            .single(),
          5000,
          'getSystemUser query direta (users eq id)'
        );
        
        console.log('📝 Resultado da query direta:', directData);
        console.log('📝 Erro da query direta:', directError);
        
        if (directError) {
          if (directError.code === 'PGRST116') {
            console.log('ℹ️ Usuário não encontrado pelo campo id');
            return null;
          }
          throw directError;
        }
        
        console.log('✅ Usuário encontrado pelo campo id:', directData);
        console.log('📝 Tipo do directData:', typeof directData);
        console.log('📝 directData é null?', directData === null);
        console.log('📝 directData é undefined?', directData === undefined);
        
        const result = { ...directData, id: directData.id.toString() } as SystemUser;
        console.log('6️⃣ Resultado final:', result);
        return result;
      }
    }

    // Step 3: Try the actual query with timeout (se auth_user_id existir)
    console.log('3️⃣ Executando query com filtro auth_user_id...');
    console.log('📝 Query: from("users").select("*").eq("auth_user_id", authUserId).single()');
    console.log('📝 authUserId sendo usado:', authUserId);
    
    const { data, error } = await withTimeout(
      supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUserId)
        .single(),
      5000,
      'getSystemUser query por auth_user_id (users eq auth_user_id)'
    );

    console.log('4️⃣ Query com filtro executada, verificando resultado...');
    console.log('📝 Dados retornados da query:', data);
    console.log('📝 Erro da query:', error);
    
    if (error) {
      console.error('❌ Erro na query com filtro:', error);
      if (error.code === 'PGRST116') {
        console.log('ℹ️ Nenhuma linha retornada (usuário não encontrado)');
        return null; // No rows returned
      }
      throw error;
    }
    
    console.log('5️⃣ Dados retornados:', data);
    
    // Garantir que o ID seja tratado como string
    const result = { ...data, id: data.id.toString() } as SystemUser;
    console.log('6️⃣ Resultado final:', result);
    
    return result;
    
  } catch (error) {
    console.error('❌ Erro em getSystemUser:', error);
    throw error;
  }
}

export async function createProfile(profileData: { user_id: string; name: string; avatar_url?: string; bio?: string }): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .insert([profileData])
    .select()
    .single();

  if (error) throw error;
  return data as Profile;
}

// Função de teste para verificar se a tabela users está acessível
export async function testUsersTable(): Promise<boolean> {
  try {
    console.log('🧪 Testando acesso à tabela users...');
    
    // Query simples para testar
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .limit(5);
    
    if (error) {
      console.error('❌ Erro ao acessar tabela users:', error);
      return false;
    }
    
    console.log('✅ Tabela users acessível');
    console.log('📝 Total de registros encontrados:', data?.length || 0);
    console.log('📝 Dados encontrados:', data);
    
    if (data && data.length > 0) {
      console.log('📝 Estrutura do primeiro registro:', Object.keys(data[0]));
      console.log('📝 Primeiro registro completo:', data[0]);
      
      // Verificar se há algum campo que possa ser o ID do usuário do Supabase
      const possibleIdFields = ['id', 'user_id', 'auth_user_id', 'supabase_user_id', 'uuid'];
      for (const field of possibleIdFields) {
        if (field in data[0]) {
          console.log(`📝 Campo ${field} encontrado com valor:`, data[0][field]);
        }
      }
    }
    
    return true;
    
  } catch (error) {
    console.error('❌ Erro ao testar tabela users:', error);
    return false;
  }
}

/**
 * Busca usuário da tabela users pelo id (users.id)
 */
export async function getSystemUserById(userId: string): Promise<SystemUser | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data ? { ...data, id: data.id.toString() } as SystemUser : null;
  } catch (error) {
    console.error('❌ Erro em getSystemUserById:', error);
    return null;
  }
}

// Função alternativa para buscar usuário por email (fallback)
export async function getSystemUserByEmail(email: string): Promise<SystemUser | null> {
  try {
    console.log('🔍 getSystemUserByEmail iniciado para email:', email);
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    
    return { ...data, id: data.id.toString() } as SystemUser;
    
  } catch (error) {
    console.error('❌ Erro em getSystemUserByEmail:', error);
    return null;
  }
}

export async function getUserProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No rows returned
    throw error;
  }
  return data as Profile;
}

export async function updateProfile(userId: string, updates: Partial<Profile>): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as Profile;
}

export async function getUserRole(userId: string): Promise<UserTenant | null> {
  try {
    const tenantId = await getTenantId();
    // Usar tenant dinâmico conforme solicitado
    const { data, error } = await supabase
      .from('user_tenants')
      .select('*')
      .eq('user_id', userId)
      .eq('tenant_id', tenantId) // Usa getTenantId() dinâmico
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw error;
    }
    return data as UserTenant;
  } catch (error) {
    console.error('❌ Erro em getUserRole:', error);
    return null;
  }
}

export async function createUserTenant(userTenantData: { user_id: string; role: UserRole; status?: string }): Promise<UserTenant> {
  try {
    const tenantId = await getTenantId();
    const { data, error } = await supabase
      .from('user_tenants')
      .insert([{
        ...userTenantData,
        tenant_id: tenantId, // Usa getTenantId() dinâmico
        status: userTenantData.status || 'active'
      }])
      .select()
      .single();

    if (error) throw error;
    return data as UserTenant;
  } catch (error) {
    console.error('❌ Erro em createUserTenant:', error);
    throw error;
  }
}

export async function upsertUserTenant(userTenantData: { user_id: string; role: UserRole; status?: string }): Promise<UserTenant> {
  try {
    const tenantId = await getTenantId();
    // Usar upsert para evitar duplicatas
    const { data, error } = await supabase
      .from('user_tenants')
      .upsert([{
        ...userTenantData,
        tenant_id: tenantId, // Usa getTenantId() dinâmico
        status: userTenantData.status || 'active'
      }], {
        onConflict: 'user_id,tenant_id'
      })
      .select()
      .single();

    if (error) throw error;
    return data as UserTenant;
  } catch (error) {
    console.error('❌ Erro em upsertUserTenant:', error);
    throw error;
  }
}

export async function updateUserRole(userId: string, newRole: UserRole): Promise<UserTenant> {
  try {
    const tenantId = await getTenantId();
    const { data, error } = await supabase
      .from('user_tenants')
      .update({ role: newRole })
      .eq('user_id', userId)
      .eq('tenant_id', tenantId) // Usa getTenantId() dinâmico
      .select()
      .single();

    if (error) throw error;
    return data as UserTenant;
  } catch (error) {
    console.error('❌ Erro em updateUserRole:', error);
    throw error;
  }
}

export async function getUserTenants(userId: string): Promise<UserTenant[]> {
  const { data, error } = await supabase
    .from('user_tenants')
    .select(`
      *,
      tenants (
        id,
        name,
        slug
      )
    `)
    .eq('user_id', userId)
    .eq('status', 'active');

  if (error) throw error;
  return data as UserTenant[];
}

export async function checkUserEnrollment(userId: string, courseId: number): Promise<boolean> {
  const { data, error } = await supabase
    .from('enrollments')
    .select('id')
    .eq('user_id', userId)
    .eq('course_id', courseId)
    .in('status', ['active', 'trial'])
    .single();

  if (error) {
    if (error.code === 'PGRST116') return false; // No enrollment found
    throw error;
  }
  return !!data;
}



export async function getUserEnrollments(userId: string): Promise<any[]> {
  const { data, error } = await supabase
    .from('enrollments')
    .select(`
      *,
      courses (
        id,
        title,
        slug,
        cover
      )
    `)
    .eq('user_id', userId)
    .in('status', ['active', 'trial']);

  if (error) throw error;
  return data || [];
}

export async function getCourseEnrollments(courseId: number): Promise<any[]> {
  const { data, error } = await supabase
    .from('enrollments')
    .select(`
      *,
      profiles (
        id,
        name,
        avatar_url
      )
    `)
    .eq('course_id', courseId)
    .in('status', ['active', 'trial'])
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// =====================================================
// FUNÇÕES DE PROGRESSO DO USUÁRIO
// =====================================================

/**
 * Busca o progresso do usuário em um curso específico
 */
export async function getUserCourseProgress(userId: string, courseId: string): Promise<{
  totalLessons: number;
  completedLessons: number;
  progressPercentage: number;
}> {
  try {
    // Primeiro, buscar os módulos do curso
    const { data: modulesData, error: modulesError } = await supabase
      .from('modules')
      .select('id')
      .eq('course_id', courseId);

    if (modulesError) throw modulesError;

    // Verificar se modulesData é válido e iterável
    if (!modulesData || !Array.isArray(modulesData)) {
      console.warn('⚠️ getUserCourseProgress: modulesData não é um array válido:', modulesData);
      return {
        totalLessons: 0,
        completedLessons: 0,
        progressPercentage: 0
      };
    }

    const moduleIds = modulesData.map(module => module.id);
    
    if (moduleIds.length === 0) {
      console.warn('⚠️ getUserCourseProgress: Nenhum módulo encontrado para o curso:', courseId);
      return {
        totalLessons: 0,
        completedLessons: 0,
        progressPercentage: 0
      };
    }

    // Buscar total de lições do curso
    const { data: totalLessonsData, error: totalError } = await supabase
      .from('lessons')
      .select('id')
      .in('module_id', moduleIds);

    if (totalError) throw totalError;

    // Verificar se totalLessonsData é válido
    if (!totalLessonsData || !Array.isArray(totalLessonsData)) {
      console.warn('⚠️ getUserCourseProgress: totalLessonsData não é um array válido:', totalLessonsData);
      return {
        totalLessons: 0,
        completedLessons: 0,
        progressPercentage: 0
      };
    }

    const lessonIds = totalLessonsData.map(lesson => lesson.id);

    // Buscar lições concluídas pelo usuário
    const { data: completedLessonsData, error: completedError } = await supabase
      .from('user_lesson_progress')
      .select('lesson_id')
      .eq('user_id', userId)
      .eq('is_completed', true)
      .in('lesson_id', lessonIds);

    if (completedError) throw completedError;

    // Verificar se completedLessonsData é válido
    if (!completedLessonsData || !Array.isArray(completedLessonsData)) {
      console.warn('⚠️ getUserCourseProgress: completedLessonsData não é um array válido:', completedLessonsData);
      return {
        totalLessons: totalLessonsData.length,
        completedLessons: 0,
        progressPercentage: 0
      };
    }

    const totalLessons = totalLessonsData.length;
    const completedLessons = completedLessonsData.length;
    const progressPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

    return {
      totalLessons,
      completedLessons,
      progressPercentage
    };

  } catch (error) {
    console.error('❌ Erro ao buscar progresso do curso:', error);
    throw error;
  }
}

/**
 * Busca o status de matrícula do usuário em um curso.
 * @param userId — id na tabela `users` (mesmo valor que enrollments.user_id / Matrícula), não o UUID do Auth.
 */
export async function getUserEnrollmentStatus(userId: string, courseId: string): Promise<{
  isEnrolled: boolean;
  isStarted: boolean;
  isCompleted: boolean;
  lastAccessedAt?: string;
}> {
  try {
    const tenantId = await getTenantId();
    const { data, error } = await supabase
      .from('enrollments')
      .select('*')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .eq('tenant_id', tenantId)
      .in('access_level', ['active', 'trial'])
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return {
        isEnrolled: false,
        isStarted: false,
        isCompleted: false,
      };
    }

    return {
      isEnrolled: true,
      isStarted: !!data.started_at,
      isCompleted: !!data.completed_at,
      lastAccessedAt: data.last_accessed_at
    };

  } catch (error) {
    console.error('❌ Erro ao buscar status de matrícula:', error);
    throw error;
  }
}

/**
 * Busca a próxima lição não concluída para continuar o curso
 */
export async function getNextLessonToContinue(
  userId: string,
  courseId: string,
  /** Quando definido, limita a estes módulos (acesso parcial). null/undefined = curso inteiro. */
  accessibleModuleIds?: string[] | null
): Promise<{
  lesson_id: string;
  lesson_title: string;
  module_id: string;
  module_title: string;
} | null> {
  const rpcParams: Record<string, unknown> = {
    p_user_id: userId,
    p_course_id: courseId,
  };
  if (accessibleModuleIds != null && accessibleModuleIds.length > 0) {
    rpcParams.p_module_ids = accessibleModuleIds;
  }

  try {
    const { data, error } = await supabase.rpc('get_next_lesson', rpcParams);

    if (error) {
      console.warn('⚠️ RPC get_next_lesson falhou, usando fallback:', error.message);
      throw error;
    }

    if (data && data.length > 0) {
      return data[0];
    }

    return null;
  } catch (error) {
    console.log('🔄 Usando fallback para buscar próxima lição...');

    try {
      let lessonsQuery = supabase
        .from('lessons')
        .select(`
          id,
          title,
          module_id,
          order,
          modules!inner (
            id,
            title,
            course_id,
            order
          )
        `)
        .eq('modules.course_id', courseId);

      if (accessibleModuleIds != null && accessibleModuleIds.length > 0) {
        lessonsQuery = lessonsQuery.in('module_id', accessibleModuleIds);
      }

      const { data: lessonsData, error: lessonsError } = await lessonsQuery;

      if (lessonsError) throw lessonsError;

      if (!lessonsData || lessonsData.length === 0) {
        return null;
      }

      const sortedLessons = [...lessonsData].sort((a, b) => {
        const moduleOrderA = Number(a.modules?.order ?? 0);
        const moduleOrderB = Number(b.modules?.order ?? 0);
        if (moduleOrderA !== moduleOrderB) {
          return moduleOrderA - moduleOrderB;
        }
        return Number(a.order ?? 0) - Number(b.order ?? 0);
      });

      const { data: completedData, error: completedError } = await supabase
        .from('user_lesson_progress')
        .select('lesson_id')
        .eq('user_id', userId)
        .eq('is_completed', true)
        .in(
          'lesson_id',
          sortedLessons.map((l) => l.id)
        );

      if (completedError) throw completedError;

      const completedLessonIds = completedData?.map((c) => c.lesson_id) || [];

      const nextLesson = sortedLessons.find(
        (lesson) => !completedLessonIds.includes(lesson.id)
      );

      if (nextLesson) {
        const mod = nextLesson.modules as { title: string };
        return {
          lesson_id: nextLesson.id,
          lesson_title: nextLesson.title,
          module_id: nextLesson.module_id,
          module_title: mod.title,
        };
      }

      return null;
    } catch (fallbackError) {
      console.error('❌ Erro no fallback de próxima lição:', fallbackError);
      return null;
    }
  }
}

/**
 * Cria matrícula do usuário em um curso
 */
export async function createCourseEnrollment(userId: string, courseId: string, tenantId: string): Promise<any> {
  try {
    const { data, error } = await supabase
      .from('enrollments')
      .insert([{
        user_id: userId,
        course_id: courseId,
        tenant_id: tenantId,
        access_level: 'active',
        started_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;

    console.log('✅ Matrícula criada:', data);
    return data;

  } catch (error) {
    console.error('❌ Erro ao criar matrícula:', error);
    throw error;
  }
}

// =====================================================
// FUNÇÕES DE FAVORITOS
// =====================================================

/**
 * Verifica se um curso está nos favoritos do usuário
 */
export async function isCourseFavorited(userId: string, courseId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_favorites')
      .select('id')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return !!data;

  } catch (error) {
    console.error('❌ Erro ao verificar favorito:', error);
    throw error;
  }
}

/**
 * Adiciona curso aos favoritos do usuário
 */
export async function addToFavorites(userId: string, courseId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_favorites')
      .insert([{
        user_id: userId,
        course_id: courseId
      }]);

    if (error) throw error;

    console.log('✅ Curso adicionado aos favoritos:', courseId);

  } catch (error) {
    console.error('❌ Erro ao adicionar favorito:', error);
    throw error;
  }
}

/**
 * Remove curso dos favoritos do usuário
 */
export async function removeFromFavorites(userId: string, courseId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_favorites')
      .delete()
      .eq('user_id', userId)
      .eq('course_id', courseId);

    if (error) throw error;

    console.log('✅ Curso removido dos favoritos:', courseId);

  } catch (error) {
    console.error('❌ Erro ao remover favorito:', error);
    throw error;
  }
}

// ==============================================
// FUNÇÕES PARA GERENCIAMENTO DE USUÁRIOS (FASE 2)
// ==============================================

export interface UserFilters {
  role?: 'student' | 'instructor' | 'admin';
  status?: 'active' | 'inactive';
  search?: string;
  tenant_id?: string;
  page?: number;
  limit?: number;
}

export interface CreateUserData {
  email: string;
  name: string;
  role?: 'student' | 'instructor' | 'admin';
  tenant_id?: string;
}

export interface UpdateUserData {
  email?: string;
  name?: string;
  role?: 'student' | 'instructor' | 'admin';
  is_active?: boolean;
}

export interface UsersListResult {
  users: SystemUser[];
  total: number;
}/**
 * Lista usuários com filtros e paginação
 */
export async function getUsers(filters: UserFilters = {}): Promise<UsersListResult> {
  try {
    const tenantId = filters.tenant_id || await getTenantId();
    const page = filters.page || 1;
    const limit = filters.limit || 50;
    const offset = (page - 1) * limit;

    let query = supabase
      .from('users')
      .select('*, user_tenants!inner(*)', { count: 'exact' })
      .eq('user_tenants.tenant_id', tenantId);

    // Filtro por busca
    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    // Filtro por role
    if (filters.role) {
      query = query.eq('user_tenants.role', filters.role);
    }

    // Filtro por status
    if (filters.status) {
      query = query.eq('user_tenants.status', filters.status);
    }

    query = query.range(offset, offset + limit - 1)
      .order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) {
      console.error('❌ Erro ao listar usuários:', error);
      throw new Error(`Erro ao listar usuários: ${error.message}`);
    }

    // Processar dados para retornar usuários com role
    const users = (data || []).map((user: any) => {
      const userTenant = user.user_tenants?.[0];
      return {
        ...user,
        role: userTenant?.role || 'student',
        status: userTenant?.status || 'active',
        is_active: userTenant?.status === 'active'
      };
    });

    return {
      users: users as SystemUser[],
      total: count || 0
    };
  } catch (error) {
    console.error('❌ Erro em getUsers:', error);
    throw error;
  }
}

/**
 * Cria um novo usuário
 */
export async function createUser(data: CreateUserData): Promise<SystemUser> {
  try {
    const tenantId = data.tenant_id || await getTenantId();

    // Validar email único
    const existingUser = await getSystemUserByEmail(data.email);
    if (existingUser) {
      throw new Error('Email já está em uso.');
    }

    // Criar usuário no Supabase Auth primeiro (se necessário)
    // Por enquanto, assumimos que o usuário já existe no Auth
    // ou será criado separadamente

    // Buscar auth_user_id se o usuário já existe no Auth
    // Se não existir, precisará ser criado no Auth primeiro
    const { data: authUser, error: authError } = await supabase.auth.admin.getUserByEmail(data.email);
    
    let authUserId: string;
    if (authError || !authUser) {
      // Se não existe no Auth, criar
      const { data: newAuthUser, error: createAuthError } = await supabase.auth.admin.createUser({
        email: data.email,
        email_confirm: true,
        user_metadata: { name: data.name }
      });

      if (createAuthError || !newAuthUser) {
        throw new Error('Erro ao criar usuário no sistema de autenticação.');
      }

      authUserId = newAuthUser.user.id;
    } else {
      authUserId = authUser.user.id;
    }

    // Criar registro em users
    const systemUser = await createSystemUser({
      auth_user_id: authUserId,
      email: data.email,
      name: data.name
    });    // Criar registro em user_tenants com role
    await createUserTenant({
      user_id: systemUser.id,
      role: data.role || 'student',
      status: 'active'
    });

    console.log('✅ Usuário criado com sucesso:', systemUser.id);
    return systemUser;
  } catch (error) {
    console.error('❌ Erro em createUser:', error);
    throw error;
  }
}

/**
 * Atualiza um usuário existente
 */
export async function updateUser(userId: string, data: UpdateUserData, currentUserId?: string): Promise<SystemUser> {
  try {
    // Verificar se o usuário existe
    const existingUser = await getSystemUserById(userId);
    if (!existingUser) {
      throw new Error('Usuário não encontrado.');
    }

    // Ao desativar (is_active=false): admin não pode desativar a si mesmo nem o último admin
    if (data.is_active === false) {
      if (currentUserId && userId === currentUserId) {
        throw new Error('Você não pode desativar sua própria conta.');
      }
      const userTenant = await getUserRole(userId);
      if (userTenant?.role === 'admin') {
        const adminCount = await getAdminCount();
        if (adminCount <= 1) {
          throw new Error('Não é possível desativar o último administrador do sistema.');
        }
      }
    }

    // Ao alterar role de admin para outro: verificar se não é o último admin
    if (data.role && data.role !== 'admin') {
      const userTenant = await getUserRole(userId);
      if (userTenant?.role === 'admin') {
        const adminCount = await getAdminCount();
        if (adminCount <= 1) {
          throw new Error('Não é possível remover o último administrador do sistema.');
        }
      }
    }

    // Validar email único se estiver sendo alterado
    if (data.email && data.email !== existingUser.email) {
      const emailUser = await getSystemUserByEmail(data.email);
      if (emailUser && emailUser.id !== userId) {
        throw new Error('Email já está em uso.');
      }
    }

    // Atualizar usuário
    const updateData: any = {};
    if (data.name !== undefined) updateData.name = data.name.trim();
    if (data.email !== undefined) updateData.email = data.email.trim();

    const { data: updatedUser, error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('❌ Erro ao atualizar usuário:', error);
      throw new Error(`Erro ao atualizar usuário: ${error.message}`);
    }

    // Atualizar role se necessário
    if (data.role) {
      await updateUserRole(userId, data.role);
    }

    // Atualizar status se necessário
    if (data.is_active !== undefined) {
      const tenantId = await getTenantId();
      const { error: statusError } = await supabase
        .from('user_tenants')
        .update({ status: data.is_active ? 'active' : 'inactive' })
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      if (statusError) {
        console.error('❌ Erro ao atualizar status:', statusError);
      }
    }

    console.log('✅ Usuário atualizado com sucesso:', updatedUser.id);
    return updatedUser as SystemUser;
  } catch (error) {
    console.error('❌ Erro em updateUser:', error);
    throw error;
  }
}

/**
 * Conta quantos administradores ativos existem no tenant
 */
export async function getAdminCount(tenantId?: string): Promise<number> {
  try {
    const tid = tenantId || await getTenantId();
    const { count, error } = await supabase
      .from('user_tenants')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tid)
      .eq('role', 'admin')
      .eq('status', 'active');

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('❌ Erro em getAdminCount:', error);
    throw error;
  }
}

/**
 * Envia email de redefinição de senha para o usuário
 */
export async function resetPasswordForUser(email: string): Promise<void> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw new Error(`Erro ao enviar email de redefinição: ${error.message}`);
    }

    console.log('✅ Email de redefinição de senha enviado para:', email);
  } catch (error) {
    console.error('❌ Erro em resetPasswordForUser:', error);
    throw error;
  }
}

/**
 * Exclui um usuário
 */
export async function deleteUser(userId: string, currentUserId?: string): Promise<void> {
  try {
    const { data: userRecord } = await supabase.from('users').select('id').eq('id', userId).single();
    if (!userRecord) {
      throw new Error('Usuário não encontrado.');
    }

    // Admin não pode excluir a si mesmo
    if (currentUserId && userId === currentUserId) {
      throw new Error('Você não pode excluir sua própria conta.');
    }

    // Verificar se é o último admin antes de excluir um admin
    const userTenant = await getUserRole(userId);
    if (userTenant?.role === 'admin') {
      const adminCount = await getAdminCount();
      if (adminCount <= 1) {
        throw new Error('Não é possível excluir o último administrador do sistema.');
      }
    }

    // Verificar se tem matrículas
    const enrollments = await getUserEnrollments(userId);
    if (enrollments && enrollments.length > 0) {
      throw new Error(
        'Não é possível excluir o usuário pois ele possui matrículas. Cancele as matrículas primeiro.'
      );
    }

    const tenantId = await getTenantId();

    // Deletar de user_tenants
    const { error: tenantError } = await supabase
      .from('user_tenants')
      .delete()
      .eq('user_id', userId)
      .eq('tenant_id', tenantId);

    if (tenantError) {
      console.warn('⚠️ Erro ao deletar user_tenants:', tenantError);
    }

    // Deletar de users
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', userId);

    if (error) {
      console.error('❌ Erro ao excluir usuário:', error);
      throw new Error(`Erro ao excluir usuário: ${error.message}`);
    }

    console.log('✅ Usuário excluído com sucesso:', userId);
  } catch (error) {
    console.error('❌ Erro em deleteUser:', error);
    throw error;
  }
}

/**
 * Ativa um usuário
 */
export async function activateUser(userId: string): Promise<void> {
  try {
    const tenantId = await getTenantId();
    const { error } = await supabase
      .from('user_tenants')
      .update({ status: 'active' })
      .eq('user_id', userId)
      .eq('tenant_id', tenantId);

    if (error) {
      console.error('❌ Erro ao ativar usuário:', error);
      throw new Error(`Erro ao ativar usuário: ${error.message}`);
    }

    console.log('✅ Usuário ativado com sucesso:', userId);
  } catch (error) {
    console.error('❌ Erro em activateUser:', error);
    throw error;
  }
}

/**
 * Desativa um usuário
 */
export async function deactivateUser(userId: string, currentUserId?: string): Promise<void> {
  try {
    // Admin não pode desativar a si mesmo
    if (currentUserId && userId === currentUserId) {
      throw new Error('Você não pode desativar sua própria conta.');
    }

    // Verificar se é o último admin antes de desativar
    const userTenant = await getUserRole(userId);
    if (userTenant?.role === 'admin') {
      const adminCount = await getAdminCount();
      if (adminCount <= 1) {
        throw new Error('Não é possível desativar o último administrador do sistema.');
      }
    }

    const tenantId = await getTenantId();
    const { error } = await supabase
      .from('user_tenants')
      .update({ status: 'inactive' })
      .eq('user_id', userId)
      .eq('tenant_id', tenantId);

    if (error) {
      console.error('❌ Erro ao desativar usuário:', error);
      throw new Error(`Erro ao desativar usuário: ${error.message}`);
    }

    console.log('✅ Usuário desativado com sucesso:', userId);
  } catch (error) {
    console.error('❌ Erro em deactivateUser:', error);
    throw error;
  }
}
